import { Form, Spin, Alert, Checkbox, Typography, Input } from 'antd';
import { Banner } from 'components/ui/Banner';
import PageMeta from 'components/PageMeta/PageMeta';
import { Document } from '@carbon/icons-react';
import Card from 'components/Card/Card';
import { DatePicker } from 'components/ui/DatePicker';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import { useAtomValue } from 'jotai';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { familyService } from 'services';
import { message } from 'antd';
import dayjs from 'dayjs';
import { getOtherAdultFirstName, getOtherAdultFullName, getApplicantFullName } from '../../utils';
import { getSpouseTitle } from './constants';
import RadioCards from 'components/RadioCards/RadioCards';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

const { Text } = Typography;

export function FamilyAppSpouseJobSearch({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const isCompletedBySponsor = application?.completedBySponsor === true;
  const unemployedSinceValue = Form.useWatch('unemployedSince', form);

  // Get spouse relationship title (spouse, ex-spouse, co-parent, etc.)
  const spouseRelationship = useMemo(() => {
    const title = getSpouseTitle(application);
    return title.charAt(0).toUpperCase() + title.slice(1);
  }, [application]);

  const onSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Use getNextStep to determine the next step
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.SPOUSE_JOB_SEARCH, _newApplication, stepConfig);
        setStep({
          applicationId: _newApplication?.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const updatedSpouse = await familyService.addUpdateAdult(
          household?.id,
          application?.additionalParents?.[0]?.id ?? null,
          {
            ...application?.additionalParents?.[0],
            unemploymentDetails: {
              unemployedSince: values.unemployedSince,
              certificationAccepted: values.certificationAccepted,
              certificationSigned: values.certificationSigned,
              certificationDate: new Date().toISOString(),
              fullTimeCareNeeded: values.fullTimeCareNeeded,
              certificationSigner: values.certificationSigner,
            },
          },
        );

        const _newApplication = await familyService.saveApplication({
          ...application,
          additionalParents: [updatedSpouse],
        });

        setApplication(_newApplication);
        _goToNextStep(_newApplication);
      } catch (error) {
        message.error('Something went wrong');
        newrelic.noticeError(error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, household?.id, readOnly, setApplication, setStep, stepConfig],
  );

  // Set initial form values
  useEffect(() => {
    if (application?.additionalParents?.[0]?.unemploymentDetails) {
      const details = application.additionalParents[0].unemploymentDetails;

      form.setFieldsValue({
        unemployedSince: details?.unemployedSince,
        fullTimeCareNeeded: details?.fullTimeCareNeeded,
        certificationAccepted: details?.certificationAccepted,
        certificationSigned: details?.certificationSigned,
        certificationSigner: details?.certificationSigner,
      });
    }
  }, [application, form]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={submitting}>
      <PageMeta title={isCompletedBySponsor ? 'Spouse Job Search Support' : 'Your Job Search Support'} />
      <Form form={form} layout="vertical" onFinish={onSubmit} disabled={readOnly}>
        <div className="p-7 pt-0">
          <Banner
            question="Job Search Support for Military Families"
            guidance={`You’ve indicated that ${getOtherAdultFirstName(
              application?.additionalParents?.[0],
            )} is actively looking for work. Fee Assistance for full time (25 hours or more per week) or part time (16-24 hours per week) child care is authorized for up to 180 days with a mandatory check in at 90 days to confirm employment status to allow spouses to look for employment.`}
            className="my-12"
          />

          <Card
            color="primary"
            title={
              <span className="flex gap-3 items-center">
                <Document size={24} className="mt-[-1px]" />
                <div>Certification of Looking for Work</div>
              </span>
            }
          >
            <div className="space-y-6">
              <Text className="block opacity-80">
                The Certification Statement must be completed and digitally signed by the sponsor or the spouse in order
                to qualify for fee assistance.
              </Text>

              <Alert
                message="IMPORTANT"
                description={
                  'The 180 day Certificate of Looking for Work form may only be utilized once by the military spouse while receiving approved fee assistance. If the military spouse is still unemployed after 180 days of looking for work, the spouse will not be awarded another period of approved fee assistance without submitting to Child Care Aware® of America their current employment or school enrollment information.'
                }
                type="warning"
                showIcon
                className="my-4"
              />

              <Form.Item
                name="fullTimeCareNeeded"
                label="I plan to enroll my child(ren) in care"
                rules={[
                  {
                    required: true,
                    message: 'Please specify whether you plan to enroll your child(ren) in care',
                  },
                ]}
              >
                <RadioCards
                  direction="horizontal"
                  options={[
                    {
                      value: true,
                      label: '25 hours or more per week',
                    },
                    {
                      value: false,
                      label: '16-24 hours per week',
                    },
                  ]}
                />
              </Form.Item>

              <div>
                <Form.Item
                  name="unemployedSince"
                  label={'On what date did you most recently become unemployed or do not qualify?'}
                  rules={[
                    {
                      required: true,
                      message: `Please select the date ${
                        isCompletedBySponsor ? 'your spouse' : 'you'
                      } became unemployed`,
                    },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();

                        if (dayjs(value).isAfter(dayjs())) {
                          return Promise.reject(new Error('Date of unemployment cannot be in the future'));
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                  className="mb-4"
                >
                  <DatePicker className="max-w-md" />
                </Form.Item>
              </div>

              <Alert
                message="90 Day Mandatory Check In Process (if no employment or school enrollment)"
                description={
                  <div>
                    <ol className="list-decimal">
                      <li>
                        <p className="max-w-[500px]">
                          Email the following information to{' '}
                          <a href="mailto:msp@usa.childcareaware.org">msp@usa.childcareaware.org</a> no more than 10
                          days before the 90 day period ends
                        </p>

                        <ol className="list-[lower-alpha] ml-5">
                          <li>
                            <p>Family ID</p>
                          </li>

                          <li>
                            <p>Name of Sponsor</p>
                          </li>

                          <li className="max-w-[600px]">
                            <p>
                              Statement “I have not secured employment or enrolled in school within 90 days. Please
                              extend my looking for work period an additional 90 days”
                            </p>
                          </li>
                        </ol>
                      </li>
                    </ol>
                  </div>
                }
                type="info"
                showIcon
                className="my-4"
              />

              <Text className="block mt-6">
                I certify that the <strong>{spouseRelationship}</strong> of{' '}
                <strong>{getApplicantFullName(application?.applicant)}</strong>,{' '}
                <strong>{getOtherAdultFullName(application?.additionalParents?.[0])}</strong> is currently looking for
                employment
                {unemployedSinceValue && (
                  <>
                    {' '}
                    and has been unemployed since <strong>{dayjs(unemployedSinceValue).format('MMMM D, YYYY')}</strong>
                  </>
                )}
                .
              </Text>

              <div className="space-y-4">
                <Form.Item
                  name="certificationAccepted"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value ? Promise.resolve() : Promise.reject('You must accept the certification to continue'),
                    },
                  ]}
                >
                  <Checkbox>
                    <Text>
                      I understand that once I am employed, I must notify Child Care Aware® of America at 1-800-793-0324
                      of my employment immediately and may not wait until the 180 days are over to do so. I understand
                      that I must report the start date of employment and report my estimated earnings. I will provide
                      copies of my pay stubs to Child Care Aware® of America for four full week's/one month's worth of
                      earnings or submit the Employment Verification Form until a month's worth of paystubs are
                      available, ensuring that the adjustment to my fee assistance reflects the correct Total Family
                      Income (TFI). I understand that any overpayment made due to my failure to notify Child Care Aware®
                      of America of my employment promptly will require my family to reimburse Child Care Aware® of
                      America for the overpayment. I understand that after 180 days, my fee assistance will be
                      discontinued if I do not find employment and provide required income documentation to Child Care
                      Aware® of America. Misrepresentation or falsifying this information may subject the individual to
                      prosecution under the Uniform Code of Military Justice (UMCJ) and/or applicable State and Federal
                      Laws.
                    </Text>
                  </Checkbox>
                </Form.Item>

                <Form.Item
                  name="certificationSigned"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(new Error('You must electronically sign to continue')),
                    },
                  ]}
                >
                  <Checkbox>
                    <Text className="table max-w-[700px]">
                      By checking this box, I electronically sign this Certification Looking for Work form and attest
                      that the above information is true and accurate.
                    </Text>
                  </Checkbox>
                </Form.Item>

                <Form.Item
                  name="certificationSigner"
                  label="Enter your full name to certify the information above"
                  rules={[{ required: true, message: 'Please enter your full name' }]}
                >
                  <Input />
                </Form.Item>
              </div>
            </div>
          </Card>
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            disabled: submitting,
            onClick: () => {
              const previousStep = getPreviousStep(FAMILY_APPLICATION_STEPS.SPOUSE_JOB_SEARCH, application, stepConfig);
              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
          }}
          nextButtonProps={{
            loading: submitting,
          }}
        />
      </Form>
    </Spin>
  );
}

export default FamilyAppSpouseJobSearch;
